import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link2, Loader2, Search, ExternalLink, Save, Check, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

/**
 * Manage the local mapping `sellsy_item_id → URL` used by the Fiches
 * Animations email template. Sellsy V2 API can't read item custom fields,
 * so this UI is the canonical place to maintain the links.
 */
const AnimationLinksSection = ({ apiUrl }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [drafts, setDrafts] = useState({}); // sellsy_item_id -> { value, saving, savedAt }

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${apiUrl}/animation-links`);
      setItems(res.data?.items || []);
    } catch (e) {
      console.error("Failed to load animation links", e);
      toast.error("Erreur lors du chargement des liens");
    }
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await axios.get(`${apiUrl}/animation-links`);
        if (!mounted) return;
        setItems(res.data?.items || []);
      } catch (e) {
        console.error("Failed to load animation links", e);
        if (mounted) toast.error("Erreur lors du chargement des liens");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [apiUrl]);

  const handleSyncFromSellsy = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${apiUrl}/animation-links/sync-from-sellsy`);
      const { scanned, upserts, deletes, errors } = res.data;
      toast.success(
        `Sync OK — ${upserts} liens importés, ${deletes} supprimés, ${scanned} items scannés${errors > 0 ? `, ${errors} erreurs` : ""}`,
        { duration: 6000 }
      );
      // Refresh list with the new values
      await fetchItems();
      setDrafts({});
    } catch (e) {
      console.error("Sync failed", e);
      const msg = e.response?.data?.detail || "Erreur lors de la synchronisation";
      toast.error(msg, { duration: 8000 });
    } finally {
      setSyncing(false);
    }
  };

  const visibleItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => it.reference.toLowerCase().includes(q));
  }, [items, search]);

  const getDraftValue = (item) => {
    const draft = drafts[item.sellsy_item_id];
    return draft && draft.value !== undefined ? draft.value : item.link || "";
  };

  const isDirty = (item) => {
    const draft = drafts[item.sellsy_item_id];
    return !!(draft && draft.value !== undefined && draft.value !== (item.link || ""));
  };

  const handleChange = (sellsy_item_id, value) => {
    setDrafts((d) => ({
      ...d,
      [sellsy_item_id]: { ...(d[sellsy_item_id] || {}), value },
    }));
  };

  const handleSave = async (item) => {
    const newValue = getDraftValue(item);
    setDrafts((d) => ({
      ...d,
      [item.sellsy_item_id]: { ...(d[item.sellsy_item_id] || {}), value: newValue, saving: true },
    }));
    try {
      await axios.put(`${apiUrl}/animation-links/${item.sellsy_item_id}`, {
        link: newValue,
      });
      // Reflect new value in the items list so isDirty becomes false next render
      setItems((prev) =>
        prev.map((it) =>
          it.sellsy_item_id === item.sellsy_item_id ? { ...it, link: newValue } : it
        )
      );
      setDrafts((d) => ({
        ...d,
        [item.sellsy_item_id]: { value: newValue, saving: false, savedAt: Date.now() },
      }));
      toast.success("Lien enregistré");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l'enregistrement");
      setDrafts((d) => ({
        ...d,
        [item.sellsy_item_id]: { ...(d[item.sellsy_item_id] || {}), saving: false },
      }));
    }
  };

  if (loading) {
    return (
      <div className="py-6 text-center">
        <Loader2 className="w-4 h-4 animate-spin mx-auto text-slate-500" />
      </div>
    );
  }

  const totalWithLink = items.filter((it) => it.link).length;

  return (
    <div data-testid="animation-links-section">
      <p className="text-xs text-slate-500 mb-2">
        Les liens viennent du champ « Lien fiche animation » de chaque fiche produit Sellsy.
        Cliquez sur « Synchroniser depuis Sellsy » pour les rapatrier en masse.
        {totalWithLink}/{items.length} liens renseignés.
      </p>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            type="button"
            disabled={syncing}
            size="sm"
            variant="outline"
            className="h-7 text-xs mb-3 w-full"
            data-testid="sync-animation-links-btn"
          >
            {syncing ? (
              <>
                <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                Synchronisation en cours...
              </>
            ) : (
              <>
                <RefreshCw className="w-3 h-3 mr-1.5" />
                Synchroniser depuis Sellsy
              </>
            )}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Synchroniser depuis Sellsy ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action remplace tous les liens locaux par ceux du champ « Lien fiche animation » de Sellsy.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleSyncFromSellsy} data-testid="confirm-sync-animation-links-btn">
              Synchroniser
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="relative mb-3">
        <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher une animation..."
          className="h-8 text-sm pl-7"
          data-testid="animation-links-search"
        />
      </div>

      <ScrollArea className="max-h-[420px] pr-2">
        <div className="space-y-1.5">
          {visibleItems.map((item) => {
            const dirty = isDirty(item);
            const saving = drafts[item.sellsy_item_id]?.saving;
            return (
              <div
                key={item.sellsy_item_id}
                className="flex items-center gap-2 p-2 bg-slate-50 rounded-md"
                data-testid={`animation-link-row-${item.sellsy_item_id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-900 truncate" title={item.reference}>
                    {item.reference}
                    {item.is_yb && (
                      <span className="ml-1.5 text-[9px] bg-amber-100 text-amber-700 px-1 rounded">YB</span>
                    )}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <Input
                      value={getDraftValue(item)}
                      onChange={(e) => handleChange(item.sellsy_item_id, e.target.value)}
                      placeholder="https://..."
                      className="h-7 text-xs"
                      data-testid={`animation-link-input-${item.sellsy_item_id}`}
                    />
                    {item.link && (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-500 hover:text-slate-900"
                        title="Ouvrir la fiche"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant={dirty ? "default" : "ghost"}
                  onClick={() => handleSave(item)}
                  disabled={saving || !dirty}
                  className="h-7 px-2 text-[10px]"
                  data-testid={`animation-link-save-${item.sellsy_item_id}`}
                >
                  {saving ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : dirty ? (
                    <Save className="w-3 h-3" />
                  ) : (
                    <Check className="w-3 h-3 text-emerald-500" />
                  )}
                </Button>
              </div>
            );
          })}
          {visibleItems.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-4">Aucune animation trouvée</p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default AnimationLinksSection;
